import AIProviderFactory from './AIProviderFactory';
import { DEFAULT_AI_CONFIG } from '../config/aiConfig';
import supabaseAILogger from '../utils/supabaseLogger';

/**
 * CV Analysis Service Facade
 * Main service interface for CV and immigration analysis
 * Handles provider selection, fallback, and error handling
 */
class CVAnalysisService {
  constructor(customConfig = null) {
    this.config = customConfig || DEFAULT_AI_CONFIG;
    this.factory = AIProviderFactory;
    this.usageLog = [];
  }

  /**
   * Analyze CV and extract structured data
   * @param {string} cvText - Raw CV text
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - Structured CV data
   */
  async analyzeCV(cvText, options = {}) {
    const startTime = Date.now();

    try {
      // Get task-specific configuration
      const taskConfig = this.config.tasks?.cvExtraction || {};
      const primaryProvider = options.provider || taskConfig.provider || this.config.primaryProvider;
      const fallbackProviders = this.config.settings?.enableFallback
        ? this.config.fallbackProviders
        : [];

      console.log(`[CV Analysis Service] Starting CV analysis with primary provider: ${primaryProvider}`);

      // Get provider with fallback support
      const provider = await this.factory.getProviderWithFallback(
        primaryProvider,
        fallbackProviders
      );

      // Merge task config with custom options
      const analysisOptions = {
        ...taskConfig,
        ...options
      };

      // Perform analysis
      const result = await provider.analyzeCV(cvText, analysisOptions);

      // Log usage (in-memory)
      this.logUsage({
        task: 'cv_extraction',
        provider: provider.getProviderName(),
        model: provider.getModelName(),
        success: !result.error,
        duration: Date.now() - startTime,
        tokens: result.usage?.totalTokens || 0,
        timestamp: new Date().toISOString()
      });

      // Log to Supabase (async, don't wait)
      supabaseAILogger.logCVExtraction(
        provider.getProviderName(),
        provider.getModelName(),
        result.usage,
        Date.now() - startTime
      ).catch(err => console.warn('[CVAnalysisService] Supabase logging failed:', err));

      return result;
    } catch (error) {
      console.error('[CV Analysis Service] CV analysis failed:', error);

      // Log failure
      this.logUsage({
        task: 'cv_extraction',
        provider: 'none',
        model: 'none',
        success: false,
        duration: Date.now() - startTime,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      // Return fallback response
      return {
        personalInfo: {},
        education: [],
        experience: [],
        skills: { technical: [], programming: [], tools: [], certifications: [] },
        summary: 'Analysis unavailable',
        strengths: [],
        languageProficiency: { english: 'unknown', other: [] },
        assessment: { score: 5, reasoning: 'Service unavailable' },
        totalExperienceYears: '',
        rawText: cvText,
        error: error.message || 'CV analysis service unavailable',
        service: 'CVAnalysisService'
      };
    }
  }

  /**
   * Analyze immigration profile based on CV and user data
   * @param {Object} userProfile - User wizard data
   * @param {Object} extractedData - Extracted CV data
   * @param {string} cvText - Raw CV text
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - Immigration analysis
   */
  async analyzeImmigrationProfile(userProfile, extractedData, cvText, options = {}) {
    const startTime = Date.now();

    try {
      // Get task-specific configuration
      const taskConfig = this.config.tasks?.immigrationAnalysis || {};
      const primaryProvider = options.provider || taskConfig.provider || this.config.primaryProvider;
      const fallbackProviders = this.config.settings?.enableFallback
        ? this.config.fallbackProviders
        : [];

      console.log(`[CV Analysis Service] Starting immigration analysis with primary provider: ${primaryProvider}`);

      // Get provider with fallback support
      const provider = await this.factory.getProviderWithFallback(
        primaryProvider,
        fallbackProviders
      );

      // Merge task config with custom options
      const analysisOptions = {
        ...taskConfig,
        ...options
      };

      // Perform analysis with retry logic
      let result;
      let retries = this.config.settings?.maxRetries || 1;
      let lastError;

      while (retries > 0) {
        try {
          result = await provider.analyzeImmigrationProfile(
            userProfile,
            extractedData,
            cvText,
            analysisOptions
          );

          // Break if successful
          if (!result.error) break;

          lastError = new Error(result.error);
          retries--;

          if (retries > 0) {
            console.warn(`[CV Analysis Service] Retrying immigration analysis (${retries} retries remaining)...`);
            await new Promise(resolve => setTimeout(resolve, this.config.settings?.retryDelay || 800));
          }
        } catch (error) {
          lastError = error;
          retries--;

          if (retries > 0) {
            console.warn(`[CV Analysis Service] Retrying immigration analysis (${retries} retries remaining)...`);
            await new Promise(resolve => setTimeout(resolve, this.config.settings?.retryDelay || 800));
          }
        }
      }

      if (result?.error && lastError) {
        throw lastError;
      }

      // Log usage (in-memory)
      this.logUsage({
        task: 'immigration_analysis',
        provider: provider.getProviderName(),
        model: provider.getModelName(),
        success: !result.error,
        duration: Date.now() - startTime,
        tokens: result.usage?.totalTokens || 0,
        timestamp: new Date().toISOString()
      });

      // Log to Supabase (async, don't wait)
      supabaseAILogger.logImmigrationAnalysis(
        provider.getProviderName(),
        provider.getModelName(),
        result.usage,
        Date.now() - startTime
      ).catch(err => console.warn('[CVAnalysisService] Supabase logging failed:', err));

      return result;
    } catch (error) {
      console.error('[CV Analysis Service] Immigration analysis failed:', error);

      // Log failure
      this.logUsage({
        task: 'immigration_analysis',
        provider: 'none',
        model: 'none',
        success: false,
        duration: Date.now() - startTime,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      // Return minimal fallback
      return {
        educationAssessment: { level: "Unknown", recognition: "Requires review" },
        experienceAnalysis: { totalYears: "0", relevantExperience: "0" },
        skillsEvaluation: { technicalSkills: {}, languageSkills: {} },
        immigrationStrengths: ["Profile under review"],
        potentialConcerns: ["Complete assessment pending"],
        recommendedPathways: [],
        selectedPathwayAnalysis: {
          pathway: userProfile?.reviewData?.selectedPathway || "Unknown",
          country: userProfile?.countrySelection?.selectedCountry || "Unknown",
          eligibilityScore: 50,
          scoreBreakdown: { education: 10, experience: 10, language: 10, financialReadiness: 10, ageOptimality: 10 }
        },
        missingCriticalInfo: ["Complete documentation required"],
        recommendedNextSteps: ["Schedule consultation with immigration specialist"],
        visaReadiness: { status: "Pending", reasoning: "Service unavailable" },
        overallAssessment: "Complete profile assessment pending due to service unavailability",
        error: error.message || 'Immigration analysis service unavailable',
        service: 'CVAnalysisService'
      };
    }
  }

  /**
   * Get available providers and their status
   * @returns {Promise<Array>} - Provider information
   */
  async getAvailableProviders() {
    return await this.factory.getHealthyProviders();
  }

  /**
   * Get service statistics
   * @returns {Object} - Service statistics
   */
  getStatistics() {
    const providerStats = this.factory.getStatistics();

    const usageStats = {
      totalRequests: this.usageLog.length,
      successfulRequests: this.usageLog.filter(log => log.success).length,
      failedRequests: this.usageLog.filter(log => !log.success).length,
      totalTokens: this.usageLog.reduce((sum, log) => sum + (log.tokens || 0), 0),
      averageDuration: this.usageLog.length > 0
        ? this.usageLog.reduce((sum, log) => sum + log.duration, 0) / this.usageLog.length
        : 0,
      byProvider: this.getUsageByProvider(),
      byTask: this.getUsageByTask()
    };

    return {
      providers: providerStats,
      usage: usageStats
    };
  }

  /**
   * Clear service cache
   * Useful when configuration changes
   */
  clearCache() {
    console.log('[CV Analysis Service] Clearing cache');
    this.factory.clearCache();
    this.usageLog = [];
  }

  /**
   * Get usage statistics by provider
   */
  getUsageByProvider() {
    const byProvider = {};

    for (const log of this.usageLog) {
      if (!byProvider[log.provider]) {
        byProvider[log.provider] = {
          requests: 0,
          successful: 0,
          failed: 0,
          totalTokens: 0
        };
      }

      byProvider[log.provider].requests++;
      if (log.success) byProvider[log.provider].successful++;
      else byProvider[log.provider].failed++;
      byProvider[log.provider].totalTokens += log.tokens || 0;
    }

    return byProvider;
  }

  /**
   * Get usage statistics by task
   */
  getUsageByTask() {
    const byTask = {};

    for (const log of this.usageLog) {
      if (!byTask[log.task]) {
        byTask[log.task] = {
          requests: 0,
          successful: 0,
          failed: 0,
          totalTokens: 0
        };
      }

      byTask[log.task].requests++;
      if (log.success) byTask[log.task].successful++;
      else byTask[log.task].failed++;
      byTask[log.task].totalTokens += log.tokens || 0;
    }

    return byTask;
  }

  /**
   * Log usage for analytics
   */
  logUsage(logEntry) {
    this.usageLog.push(logEntry);

    // Keep only last 1000 entries
    if (this.usageLog.length > 1000) {
      this.usageLog = this.usageLog.slice(-1000);
    }

    // Log to console if enabled
    if (this.config.settings?.logUsage) {
      console.log('[CV Analysis Service] Usage:', logEntry);
    }
  }

  /**
   * Clear usage logs
   */
  clearUsageLogs() {
    this.usageLog = [];
  }
}

// Export singleton instance
const cvAnalysisService = new CVAnalysisService();

export default cvAnalysisService;
export { CVAnalysisService };
